/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Router middleware for dispatching route changes
 */

import { findMatch } from '@zcode/router-utils'
import type { Dispatch as StateTypeDispatch, State as StateTypeState } from '@zcode/state-type'
import type { Values } from '@zcode/utility-types'
import type { History, UnregisterCallback } from 'history'
import qs from 'querystringify'
import type { Middleware, Dispatch as ReduxDispatch } from 'redux'
import type { Action, ExtractActions } from 'redux-actions'
import type * as actions from './actions'
import {
  DELETE_HASH,
  DELETE_HASH_QUERY,
  DELETE_QUERY,
  GET_ROUTE_PROPS,
  GO,
  GO_BACK,
  GO_FORWARD,
  LISTEN,
  MATCH,
  PUSH,
  PUSH_HASH,
  PUSH_SEARCH,
  REPLACE,
  REPLACE_HASH,
  REPLACE_SEARCH,
  SET_HASH_QUERY,
  SET_QUERY,
  START_HISTORY_MIDDLEWARE,
  STOP_HISTORY_MIDDLEWARE,
  UNLISTEN,
} from './constants'

export type HistoryActions =
  | ExtractActions<
      Values<
        Pick<
          typeof actions,
          | 'deleteHash'
          | 'deleteHashQuery'
          | 'deleteQuery'
          | 'getRouteProps'
          | 'go'
          | 'goBack'
          | 'goForward'
          | 'listen'
          | 'push'
          | 'pushHash'
          | 'pushSearch'
          | 'replace'
          | 'replaceHash'
          | 'replaceSearch'
          | 'setHashQuery'
          | 'setQuery'
          | 'startHistoryMiddleware'
          | 'stopHistoryMiddleware'
          | 'unlisten'
        >
      >
    >
  | Action<
      typeof MATCH,
      {
        func: (routeProps: actions.RouteProps, state: any, dispatch: any) => any
        routes: actions.Route[]
      }
    >

export type HistoryResultType<
  Action extends HistoryActions,
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
> = Action extends ExtractActions<
  (typeof actions)['startHistoryMiddleware'] | (typeof actions)['listen']
>
  ? UnregisterCallback
  : Action extends ExtractActions<
        (typeof actions)['stopHistoryMiddleware'] | (typeof actions)['unlisten']
      >
    ? UnregisterCallback | undefined
    : Action extends {
          payload: { func: (routeProps: any, state: any, dispatch: any) => infer Return }
          type: typeof MATCH
        }
      ? Return
      : Action extends actions.GetRoutePropsAction<{ routes?: undefined } | undefined>
        ? actions.RoutePropsWithoutMatch<Params>
        : Action extends actions.GetRoutePropsAction<any>
          ? actions.RouteProps<Params>
          : Action extends ExtractActions<
                | (typeof actions)['deleteHash']
                | (typeof actions)['deleteHashQuery']
                | (typeof actions)['deleteQuery']
                | (typeof actions)['go']
                | (typeof actions)['goBack']
                | (typeof actions)['goForward']
                | (typeof actions)['push']
                | (typeof actions)['pushHash']
                | (typeof actions)['pushSearch']
                | (typeof actions)['replace']
                | (typeof actions)['replaceHash']
                | (typeof actions)['replaceSearch']
                | (typeof actions)['setHashQuery']
                | (typeof actions)['setQuery']
              >
            ? undefined
            : never

export type HistoryMiddlewareDispatch = <
  Action extends HistoryActions,
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
>(
  action: Action
) => HistoryResultType<Action, Params>

const stringifyHashQuery = (hashQuery: Record<string, unknown>) => {
  if (hashQuery) {
    return qs.stringify(hashQuery, '#').replace(/[&]/g, '#')
  }
  return ''
}
// TODO: Add a disableListening option to disable auto-listening
// TODO: Add a new LOCATION_CHANGED action for dispatching location changes
const createHistoryMiddleware = <
  State = StateTypeState,
  Dispatch extends ReduxDispatch = StateTypeDispatch,
>(
  history: History<unknown>
): Middleware<HistoryMiddlewareDispatch, State, Dispatch> => {
  let { location } = history
  let hashQuery: Record<string, unknown> = {}
  let searchQuery: Record<string, unknown> = {}
  const listen = () =>
    history.listen((currentLocation) => {
      const { hash, search } = currentLocation
      location = currentLocation
      if (typeof hash === 'string' && hash.includes('#')) {
        hashQuery = qs.parse(hash.replace('#', ''))
      } else {
        hashQuery = {}
      }
      searchQuery = qs.parse(search)
    })
  let unlisten: ReturnType<typeof listen> | undefined = listen()
  const listenerMap = new WeakMap<object, UnregisterCallback>()
  return (store) => (next) => (action: HistoryActions) => {
    if (!action) {
      return next(action)
    }
    switch (action.type) {
      case GO: {
        return history.go(action.payload)
      }
      case GO_BACK: {
        return history.goBack()
      }
      case GO_FORWARD: {
        return history.goForward()
      }
      case DELETE_HASH_QUERY: {
        const key = action.payload?.key
        if (key !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete hashQuery[key]
          return history.push({
            ...location,
            hash: stringifyHashQuery(hashQuery),
          })
        }
        return
      }
      case PUSH_HASH: {
        const hash = action.payload?.hash
        if (hash !== undefined) {
          return history.push({
            ...location,
            hash: `#${hash}`,
          })
        }
        return
      }
      case REPLACE_HASH: {
        const hash = action.payload?.hash
        if (hash !== undefined) {
          return history.replace({
            ...location,
            hash: `#${hash}`,
          })
        }
        return
      }
      case DELETE_HASH: {
        return history.replace({
          ...location,
          hash: undefined,
        })
      }
      case SET_HASH_QUERY: {
        const key = action.payload?.key
        const value = action.payload?.value
        const replace = action.payload?.replace
        if (key !== undefined && value !== undefined) {
          if (hashQuery[key] !== value) {
            hashQuery[key] = value
            const newLocation = {
              ...location,
              hash: stringifyHashQuery(hashQuery),
            }
            if (replace) {
              return history.replace(newLocation)
            }
            return history.push(newLocation)
          }
        }
        return
      }
      case DELETE_QUERY: {
        const key = action.payload?.key
        if (key !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete searchQuery[key]
          return history.push({
            ...location,
            search: qs.stringify(searchQuery, true),
          })
        }
        return
      }
      case PUSH_SEARCH: {
        const search = action.payload?.search
        if (search !== undefined) {
          return history.push({
            ...location,
            search: `?${search}`,
          })
        }
        return
      }
      case REPLACE_SEARCH: {
        const search = action.payload?.search
        if (search !== undefined) {
          return history.replace({
            ...location,
            search: `?${search}`,
          })
        }
        return
      }
      case SET_QUERY: {
        const key = action.payload?.key
        const value = action.payload?.value
        const replace = action.payload?.replace
        if (key !== undefined && value !== undefined) {
          if (searchQuery[key] !== value) {
            searchQuery[key] = value
            const newLocation = {
              ...location,
              search: qs.stringify(searchQuery, true),
            }
            if (replace) {
              return history.replace(newLocation)
            }
            return history.push(newLocation)
          }
        }
        return
      }
      case MATCH: {
        const { func, routes } = action.payload
        // NOTE: the location here is "kind of" immutable but it isn't the same immutable location
        // that react-router uses. If you are using strict equality to compare this location to one
        // you got from withRouter it will appear different even though it isn't.
        //
        // This matters for bubble detail location freezing, where freezing and thawing the location
        // will cause everything to recalculate even though the location hasn't changed.
        const routeProps: actions.RouteProps = {
          history,
          location,
          match: routes ? findMatch(routes, location) : null,
        }
        const { dispatch, getState } = store
        return func(routeProps, dispatch, getState)
      }
      case PUSH: {
        // @ts-expect-error there are 2 overloads; TS doesn't quite seem to get it
        return history.push(...action.payload)
      }
      case REPLACE: {
        // @ts-expect-error there are 2 overloads; TS doesn't quite seem to get it
        return history.replace(...action.payload)
      }
      case START_HISTORY_MIDDLEWARE: {
        unlisten?.()
        unlisten = listen()
        return unlisten
      }
      case STOP_HISTORY_MIDDLEWARE: {
        const response = unlisten?.()
        unlisten = undefined
        return response
      }
      case LISTEN: {
        const callback = action.payload
        const unlisten = history.listen(callback)
        listenerMap.set(callback, unlisten)
        return unlisten
      }
      case UNLISTEN: {
        const callback = action.payload
        const unlisten = listenerMap.get(callback)
        return unlisten?.()
      }
      case GET_ROUTE_PROPS: {
        const routes = action.payload?.routes
        return {
          history,
          location,
          match: routes ? findMatch(routes, location) : null,
        }
      }
      default:
        return next(action)
    }
  }
}
export default createHistoryMiddleware
